import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Lock, Loader2, Trash2, Upload, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, imgSrc, uploadFile } from "@/lib/api";
import { t } from "@/lib/i18n";
import { toast } from "sonner";

const KINDS = ["letter", "certificate", "land_record", "other"];
const KIND_LABEL = { letter: "Letter", certificate: "Certificate", land_record: "Land record", other: "Other" };
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const DocForm = ({ personId, onAdded }) => {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ title: "", kind: "letter", year: "" });
  const [progress, setProgress] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error(t("Choose a PDF"));
    setProgress(0);
    try {
      const { path } = await uploadFile(file, "document", setProgress);
      await api.post(`/persons/${personId}/documents`, { path, title: f.title, kind: f.kind, year: f.year ? parseInt(f.year, 10) : null });
      setFile(null); setF({ title: "", kind: "letter", year: "" });
      toast.success(t("Document uploaded"));
      onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setProgress(null); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-[#F4EDE2] p-5 grid md:grid-cols-[auto_1fr_150px_100px_auto] gap-3 items-center" data-testid="document-form">
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="document-file-input" />
      <Button type="button" variant="outline" onClick={() => ref.current?.click()} className="rounded-full bg-white max-w-[220px] truncate" data-testid="document-pick-btn">
        <FileText className="w-4 h-4 mr-2 shrink-0" /><span className="truncate">{file ? file.name : t("Choose a PDF")}</span>
      </Button>
      <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t("Document title")} className="h-11 rounded-xl bg-white" data-testid="document-title-input" />
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-11 rounded-xl border border-[#E5DEC9] bg-white px-3 text-sm" data-testid="document-kind-select">
        {KINDS.map((k) => <option key={k} value={k}>{t(KIND_LABEL[k])}</option>)}
      </select>
      <Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} placeholder={t("Year")} className="h-11 rounded-xl bg-white" data-testid="document-year-input" />
      <Button type="submit" disabled={progress !== null} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20] h-11 px-5" data-testid="document-submit-btn">
        {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{progress}%</> : <><Upload className="w-4 h-4 mr-2" />{t("Upload PDF")}</>}
      </Button>
    </form>
  );
};

export const PersonDocuments = ({ personId }) => {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const member = !!user?.can_vote;
  const load = useCallback(() => api.get(`/persons/${personId}/documents`).then(({ data }) => setDocs(data)), [personId]);
  useEffect(() => { if (member) load(); }, [member, load]);
  const del = async (d) => {
    if (!window.confirm(`${t("Delete")}: ${d.title}?`)) return;
    try { await api.delete(`/documents/${d.doc_id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-5" data-testid="person-documents">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />{t("Family documents")}</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{t("Letters, certificates & land records")}</h2>
      </div>
      {!member ? (
        <p className="text-sm text-[#7E8E85] flex items-center gap-1.5" data-testid="documents-locked"><Lock className="w-3.5 h-3.5" />{t("Documents are private to approved family members.")}</p>
      ) : (
        <>
          {docs.length === 0 ? <p className="text-[#7E8E85]" data-testid="documents-empty">{t("No documents yet.")}</p> : (
            <ul className="divide-y divide-[#E5DEC9] rounded-3xl border border-[#E5DEC9] bg-white" data-testid="documents-list">
              {docs.map((d) => (
                <li key={d.doc_id} className="flex items-center gap-4 p-4" data-testid={`document-${d.doc_id}`}>
                  <span className="w-11 h-11 shrink-0 rounded-xl bg-[#C62828]/10 grid place-items-center"><FileText className="w-5 h-5 text-[#C62828]" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{d.title}</p>
                    <p className="text-xs text-[#7E8E85]">{t(KIND_LABEL[d.kind] || "Other")}{d.year ? ` · ${d.year}` : ""} · {size(d.size)} · {t("by")} {d.uploader}</p>
                  </div>
                  <Button asChild size="sm" variant="outline" className="rounded-full" data-testid={`document-open-${d.doc_id}`}>
                    <a href={imgSrc(d.url)} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1" />{t("Open")}</a>
                  </Button>
                  {(user.is_admin || user.user_id === d.uploaded_by) && (
                    <Button size="icon" variant="ghost" className="text-[#C62828]" onClick={() => del(d)} data-testid={`document-delete-${d.doc_id}`}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <DocForm personId={personId} onAdded={load} />
        </>
      )}
    </section>
  );
};
